import { Button, IconLoading } from '@harvesthq/porchlight'
import moment from 'moment'
import {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'preact/compat'

import { HarvestAppApi } from '../lib/api'
import { accessToken, currentAccountId } from '../lib/authentication'
import { broadcastMessageFrameClose } from '../lib/broadcastMessage'
import { buildExternalReferenceFromParams } from '../lib/helpers/service'
import { formattedTime } from '../lib/helpers/time'

const FiveSeconds = 1000 * 5

export const RunningTimer = ({
  closable,
  onStopTimer,
  timer: runningTimer,
  wantsTimestampTimers,
  params,
}) => {
  const calculateDuration = useCallback(() => {
    const msSinceStarted = moment().diff(
      moment.utc(runningTimer.timer_started_at)
    )
    return wantsTimestampTimers
      ? msSinceStarted
      : moment.duration(msSinceStarted).add(runningTimer.hours, 'hours')
  }, [runningTimer.hours, runningTimer.timer_started_at, wantsTimestampTimers])
  const [stopping, setStopping] = useState(false)
  const [duration, setDuration] = useState(calculateDuration())

  const buttonLabel = stopping ? 'Saving…' : 'Stop timer'
  const { project, client, task, external_reference, notes, started_time } =
    runningTimer

  const stopTimer = () => {
    if (!runningTimer || !accessToken || !currentAccountId) return

    setStopping(true)

    const externalReference = buildExternalReferenceFromParams(params)

    const client = new HarvestAppApi(accessToken, currentAccountId)
    client
      .patch(`/time_entries/${runningTimer.id}/stop`, {
        external_reference: externalReference,
      })
      .then(() => {
        onStopTimer(runningTimer)
      })
  }

  const formattedDuration = useMemo(
    () => formattedTime(moment.duration(duration).asHours()),
    [duration]
  )

  const button = useRef()

  useEffect(() => {
    button.current.focus()
  }, [])

  useEffect(() => {
    const interval = setInterval(() => {
      setDuration(calculateDuration())
    }, FiveSeconds)
    return () => clearInterval(interval)
  })

  useEffect(() => {
    const interval = setInterval(() => {
      if (!runningTimer || !accessToken || !currentAccountId) return

      const client = new HarvestAppApi(accessToken, currentAccountId)
      client
        .get('/platform/running_time_entry')
        .then((entry) => {
          if (entry === null || entry.id !== runningTimer.id)
            window.location.reload()
        })
        .catch(() => window.location.reload())
    }, FiveSeconds * 12)
    return () => clearInterval(interval)
  }, [runningTimer])

  return (
    <>
      <div className="running-timer pds-flex-list pds-justify-between pds-items-end">
        <div>
          <h2 className="pds-mb-xs">
            {project.name}
            <span className="pds-ml-xs pds-weight-normal">({client.name})</span>
          </h2>

          <h4 className="pds-mb-xs pds-color-muted pds-weight-normal">
            {task.name}
          </h4>
          <p className="pds-color-muted">
            {external_reference?.permalink?.length ? (
              <a
                href={external_reference.permalink}
                target="_blank"
                rel="noopener"
              >
                {external_reference.service_icon_url?.length && (
                  <img
                    alt={`Icon of the ${external_reference.service} service.`}
                    className="pds-mr-xs"
                    src={external_reference.service_icon_url}
                    height="15"
                    width="15"
                  />
                )}
                {notes}
              </a>
            ) : (
              notes
            )}
          </p>
        </div>
        <div className="formatted-duration">
          <p className="pds-h1">{formattedDuration}</p>
          {wantsTimestampTimers && (
            <p className="pds-color-muted">
              Started at <strong>{started_time}</strong>
            </p>
          )}
        </div>
      </div>

      <div className="pds-flex pds-mt-sm">
        <button
          ref={button}
          className="pds-button pds-button-running"
          disabled={stopping}
          onClick={stopTimer}
          aria-label="Stop timer"
        >
          {stopping && <IconLoading className="pds-mr-xs" />}
          {buttonLabel}
        </button>
        {closable && (
          <Button
            label="Cancel"
            variant="cancel"
            className="pds-ml-sm"
            onClick={broadcastMessageFrameClose}
            data-analytics-element-id="timesheet-day-entry-editor-cancel"
          />
        )}
      </div>
    </>
  )
}
